import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { FileSearch, Eye, ShieldAlert, Network, Lightbulb, Zap, HardHat, Package, Mountain, Wrench, Building2, Zap as EnergyIcon, Binary, Users } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function LightSections() {
  return <div className="bg-light text-navy" data-id="element-261">
      {/* PROBLEMA */}
      <section id="problema" className="py-24" data-id="element-262">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-263">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-264">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 leading-tight" data-id="element-265">
              Las licitaciones no se ganan en terreno.
              <br data-id="element-266" />
              <span className="text-techblue" data-id="element-267">
                Se ganan entendiendo mejor la información.
              </span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto" data-id="element-268">
              Cada licitación importante contiene cientos de planos,
              especificaciones, contratos, anexos y respuestas a consultas. Los
              equipos de propuestas tienen días o semanas para encontrar
              información crítica mientras compiten contra múltiples empresas.
              Las decisiones incorrectas generan pérdidas millonarias.
            </p>
          </motion.div>
        </div>
      </section>

      {/* INSIGHT */}
      <section className="py-24 bg-white border-y border-slate-200" data-id="element-269">
        <div className="container mx-auto px-4" data-id="element-270">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-271">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight" data-id="element-272">
              Las empresas creen que están estudiando una licitación.
              <br data-id="element-273" />
              <span className="text-slate-500" data-id="element-274">
                En realidad están tomando una decisión financiera.
              </span>
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto" data-id="element-275">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8" data-id="element-276">
              <div className="flex-1 bg-slate-50 p-8 rounded-xl border border-slate-200 text-center w-full" data-id="element-277">
                <div className="text-techblue font-semibold mb-2" data-id="element-278">Paso 1</div>
                <div className="text-xl font-bold" data-id="element-279">Documentos</div>
              </div>

              <motion.div initial={{
              width: 0,
              opacity: 0
            }} whileInView={{
              width: 40,
              opacity: 1
            }} viewport={{
              once: true
            }} className="hidden md:block h-0.5 bg-slate-300 relative" data-id="element-280">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 border-t-2 border-r-2 border-slate-300 rotate-45" data-id="element-281" />
              </motion.div>

              <div className="flex-1 bg-slate-50 p-8 rounded-xl border border-slate-200 text-center w-full" data-id="element-282">
                <div className="text-techblue font-semibold mb-2" data-id="element-283">Paso 2</div>
                <div className="text-xl font-bold" data-id="element-284">Decisiones</div>
              </div>

              <motion.div initial={{
              width: 0,
              opacity: 0
            }} whileInView={{
              width: 40,
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="hidden md:block h-0.5 bg-slate-300 relative" data-id="element-285">
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 border-t-2 border-r-2 border-slate-300 rotate-45" data-id="element-286" />
              </motion.div>

              <div className="flex-1 bg-navy text-white p-8 rounded-xl border border-navy text-center w-full shadow-lg" data-id="element-287">
                <div className="text-cyan font-semibold mb-2" data-id="element-288">Paso 3</div>
                <div className="text-xl font-bold" data-id="element-289">Resultado económico</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUPERPODER */}
      <section className="py-24" data-id="element-290">
        <div className="container mx-auto px-4" data-id="element-291">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center max-w-3xl mx-auto mb-16" data-id="element-292">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-6" data-id="element-293">
              Tu equipo sigue siendo el mismo.
              <br data-id="element-294" />
              <span className="text-techblue" data-id="element-295">
                Su capacidad de análisis ya no.
              </span>
            </h2>
            <p className="text-lg text-slate-600" data-id="element-296">
              Prisma entrega un nivel de entendimiento imposible de lograr
              manualmente bajo presión.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-297">
            {[{
            icon: FileSearch,
            text: 'Detecta contradicciones.'
          }, {
            icon: Eye,
            text: 'Encuentra requisitos ocultos.'
          }, {
            icon: ShieldAlert,
            text: 'Identifica riesgos contractuales.'
          }, {
            icon: Network,
            text: 'Relaciona información dispersa.'
          }, {
            icon: Lightbulb,
            text: 'Sugiere consultas estratégicas.'
          }, {
            icon: Zap,
            text: 'Resume miles de páginas en minutos.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-id="element-298">
                <item.icon className="w-8 h-8 text-techblue mb-4" data-id="element-299" />
                <h3 className="text-lg font-semibold" data-id="element-300">{item.text}</h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="py-24 bg-white border-y border-slate-200" data-id="element-301">
        <div className="container mx-auto px-4 max-w-5xl" data-id="element-302">
          <div className="grid md:grid-cols-3 gap-8" data-id="element-303">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="relative" data-id="element-304">
              <div className="text-6xl font-bold text-slate-100 absolute -top-6 -left-4 z-0" data-id="element-305">
                1
              </div>
              <div className="relative z-10" data-id="element-306">
                <h3 className="text-xl font-bold mb-4" data-id="element-307">Sube la licitación.</h3>
                <p className="text-slate-600 text-sm" data-id="element-308">
                  Carga todos los documentos sin importar el formato o volumen.
                </p>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: 0.2
          }} className="relative" data-id="element-309">
              <div className="text-6xl font-bold text-slate-100 absolute -top-6 -left-4 z-0" data-id="element-310">
                2
              </div>
              <div className="relative z-10" data-id="element-311">
                <h3 className="text-xl font-bold mb-4" data-id="element-312">Prisma analiza:</h3>
                <div className="flex flex-wrap gap-2" data-id="element-313">
                  {['bases administrativas', 'bases técnicas', 'contratos', 'memorias', 'planos', 'anexos', 'consultas'].map(chip => <span key={chip} className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full border border-slate-200" data-id="element-314">
                      {chip}
                    </span>)}
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: 0.4
          }} className="relative" data-id="element-315">
              <div className="text-6xl font-bold text-slate-100 absolute -top-6 -left-4 z-0" data-id="element-316">
                3
              </div>
              <div className="relative z-10" data-id="element-317">
                <h3 className="text-xl font-bold mb-4" data-id="element-318">Obtén:</h3>
                <ul className="space-y-2" data-id="element-319">
                  {['riesgos', 'alertas', 'contradicciones', 'oportunidades', 'inteligencia accionable'].map(item => <li key={item} className="flex items-center gap-2 text-sm font-medium text-navy" data-id="element-320">
                      <div className="w-1.5 h-1.5 rounded-full bg-techblue" data-id="element-321" />
                      <span className="capitalize" data-id="element-322">{item}</span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PARA QUIÉN ES */}
      <section id="para-quien" className="py-24" data-id="element-323">
        <div className="container mx-auto px-4" data-id="element-324">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-325">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" data-id="element-326">
              Para quién es Prisma.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto" data-id="element-327">
            {[{
            icon: HardHat,
            text: 'Constructoras'
          }, {
            icon: Package,
            text: 'Proveedores'
          }, {
            icon: Mountain,
            text: 'Minería'
          }, {
            icon: Wrench,
            text: 'Montajes industriales'
          }, {
            icon: Building2,
            text: 'Infraestructura'
          }, {
            icon: EnergyIcon,
            text: 'Energía'
          }, {
            icon: Binary,
            text: 'Ingeniería'
          }, {
            icon: Users,
            text: 'Contratistas'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-white p-6 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center gap-3 hover:border-techblue hover:shadow-sm transition-colors" data-id="element-328">
                <item.icon className="w-8 h-8 text-slate-400" data-id="element-329" />
                <span className="font-semibold text-sm" data-id="element-330">{item.text}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* VISIÓN */}
      <section id="vision" className="py-32 bg-white border-t border-slate-200" data-id="element-331">
        <div className="container mx-auto px-4 max-w-4xl text-center" data-id="element-332">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-333">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight mb-10" data-id="element-334">
              Estamos construyendo el sistema operativo de inteligencia para
              licitaciones.
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-slate-600 leading-relaxed" data-id="element-335">
              <p data-id="element-336">
                Nuestra visión es que ninguna empresa presente una oferta
                relevante sin pasar antes por Prisma.
              </p>
              <p data-id="element-337">
                Así como hoy nadie realiza una due diligence sin análisis
                financiero.
              </p>
              <p className="font-semibold text-navy" data-id="element-338">
                Mañana nadie debería presentar una oferta sin inteligencia
                documental.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}